import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage, DEFAULT_LOCALE, type Locale } from '@/hooks/useLanguage';

interface OriginOption {
  id: string;
  productId: string;
  productSlug: string;
  label: string;
  flag: string;
  flagType: 'emoji' | 'img';
  sortOrder: number;
}

interface OriginGroup {
  id: string;
  groupKey: string;
  selectorLabel: string;
  options: OriginOption[];
}

// Cache to avoid re-fetching
let cachedGroups: OriginGroup[] | null = null;
let cachedLocale: string | null = null;

function FlagIcon({ flag, flagType, label }: { flag: string; flagType: string; label: string }) {
  if (flagType === 'img') {
    return (
      <img
        src={`/flags/${flag}.svg`}
        alt={label}
        className="h-4 w-5 rounded-[2px] object-cover"
      />
    );
  }
  return <span className="text-base leading-none">{flag}</span>;
}

interface OriginSelectorProps {
  currentSlug: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

export default function OriginSelector({ currentSlug, isOpen, onToggle }: OriginSelectorProps) {
  const navigate = useNavigate();
  const { locale } = useLanguage();
  const [internalOpen, setInternalOpen] = useState(false);
  const [groups, setGroups] = useState<OriginGroup[]>(cachedGroups || []);
  const [loading, setLoading] = useState(!cachedGroups || cachedLocale !== locale);

  const open = isOpen !== undefined ? isOpen : internalOpen;
  const toggleOpen = onToggle || (() => setInternalOpen(o => !o));

  useEffect(() => {
    if (cachedGroups && cachedLocale === locale) {
      setGroups(cachedGroups);
      setLoading(false);
      return;
    }

    async function fetch() {
      // Fetch groups with translations
      const { data: groupsData } = await supabase
        .from('product_origin_groups')
        .select(`
          id, group_key, selector_label, sort_order,
          product_origin_group_translations(locale, selector_label)
        `)
        .order('sort_order');

      // Fetch options with translations and product slugs
      const { data: optionsData } = await supabase
        .from('product_origin_options')
        .select(`
          id, group_id, product_id, label, flag, flag_type, sort_order,
          product_origin_option_translations(locale, label),
          products!inner(slug)
        `)
        .order('sort_order');

      if (!groupsData || !optionsData) {
        setLoading(false);
        return;
      }

      const optionProductIds = Array.from(new Set(optionsData.map((o: any) => o.product_id)));
      let translatedSlugByProductId: Record<string, string> = {};

      if (locale !== DEFAULT_LOCALE && optionProductIds.length > 0) {
        const { data: translationRows } = await supabase
          .from('product_translations')
          .select('product_id, slug')
          .eq('locale', locale)
          .in('product_id', optionProductIds);

        if (translationRows) {
          translatedSlugByProductId = translationRows.reduce((acc: Record<string, string>, row: any) => {
            if (row.slug?.trim()) acc[row.product_id] = row.slug;
            return acc;
          }, {});
        }
      }

      const result: OriginGroup[] = groupsData.map((g: any) => {
        // Get translated selector label
        const translation = g.product_origin_group_translations?.find(
          (t: any) => t.locale === locale
        );
        const selectorLabel = (locale !== DEFAULT_LOCALE && translation?.selector_label) || g.selector_label;

        // Get options for this group
        const options: OriginOption[] = optionsData
          .filter((o: any) => o.group_id === g.id)
          .map((o: any) => {
            const optTranslation = o.product_origin_option_translations?.find(
              (t: any) => t.locale === locale
            );
            return {
              id: o.id,
              productId: o.product_id,
              productSlug: translatedSlugByProductId[o.product_id] || o.products?.slug || '',
              label: (locale !== DEFAULT_LOCALE && optTranslation?.label) || o.label,
              flag: o.flag,
              flagType: o.flag_type as 'emoji' | 'img',
              sortOrder: o.sort_order,
            };
          });

        return {
          id: g.id,
          groupKey: g.group_key,
          selectorLabel,
          options,
        };
      });

      cachedGroups = result;
      cachedLocale = locale;
      setGroups(result);
      setLoading(false);
    }

    fetch();
  }, [locale]);

  // Find which group the current slug belongs to
  const currentGroup = groups.find(g =>
    g.options.some(o => o.productSlug === currentSlug)
  );

  if (!currentGroup || loading) return null;

  const currentOption = currentGroup.options.find(o => o.productSlug === currentSlug);
  const productPrefix = locale === 'de' ? 'produkt' : locale === 'en' ? 'product' : locale === 'it' ? 'prodotto' : 'produit';

  return (
    <div className="mb-4 flex items-center gap-4 border-b pb-4">
      <label className="w-36 shrink-0 text-sm font-semibold">
        {currentGroup.selectorLabel}
      </label>
      <div className="relative flex-1" data-dropdown>
        <button
          onClick={toggleOpen}
          className="flex w-full items-center justify-between rounded-lg border bg-card px-4 py-2.5 text-sm transition-all hover:border-primary/40"
        >
          <span className="flex items-center gap-2.5">
            {currentOption && <FlagIcon flag={currentOption.flag} flagType={currentOption.flagType} label={currentOption.label} />}
            {currentOption?.label ?? 'Bitte auswählen'}
          </span>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute z-20 mt-1 w-full rounded-lg border bg-card shadow-lg">
            {currentGroup.options.map(option => {
              const isActive = option.productSlug === currentSlug;
              return (
                <button
                  key={option.id}
                  onClick={() => {
                    if (onToggle) onToggle(); else setInternalOpen(false);
                    if (!isActive) {
                      const prefix = locale === 'de' ? '' : `/${locale}`;
                      navigate(`${prefix}/${productPrefix}/${option.productSlug}`);
                    }
                  }}
                  className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-all first:rounded-t-lg last:rounded-b-lg ${
                    isActive
                      ? 'bg-primary font-semibold text-primary-foreground'
                      : 'text-foreground hover:bg-accent'
                  }`}
                >
                  <FlagIcon flag={option.flag} flagType={option.flagType} label={option.label} />
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
